// Academic Management Service - Rattrapages, Absences, and Parameters
import { apiGet, apiPatch, apiPut, API_BASE, getAuthHeaders } from '@/utils/api';

// Types
export interface Rattrapage {
  id: string;
  studentId: string;
  studentCode: string;
  studentFirstName: string;
  studentLastName: string;
  moduleElementId: string;
  moduleElementName: string;
  moduleName: string;
  moduleCode: string;
  groupeId: string;
  groupeName: string;
  filiereId: string;
  filiereCode: string;
  filiereName: string;
  niveau: number;
  reason: 'FAILED_GRADE' | 'EXCESSIVE_ABSENCE' | 'FAILED_COMPONENT' | 'MANUAL';
  originalGrade: number | null;
  absenceCount: number | null;
  examDate: string | null;
  examLocation: string | null;
  rattrapageGrade: number | null;
  status: 'PENDING' | 'SCHEDULED' | 'COMPLETED' | 'PASSED' | 'FAILED' | 'CANCELLED';
  comments: string | null;
  createdAt: string;
}

export interface RattrapageSummary {
  total: number;
  pending: number;
  scheduled: number;
  completed: number;
  passed: number;
  failed: number;
  cancelled: number;
  byReason: {
    failedGrade: number;
    excessiveAbsence: number;
    failedComponent: number;
    manual: number;
  };
}

export interface AttendanceRecord {
  id: string;
  studentId: string;
  studentCode: string;
  studentFirstName: string;
  studentLastName: string;
  seanceId: string;
  seanceDate: string;
  seanceStartTime: string;
  seanceEndTime: string;
  moduleElementName: string;
  moduleName: string;
  groupeName: string;
  filiereName: string;
  niveau: number;
  professorName: string;
  status: 'NOT_MARKED' | 'PRESENT' | 'ABSENT' | 'LATE' | 'EXCUSED';
  justification: string | null;
  markedAt: string | null;
  markedBy: string | null;
}

export interface AbsenceSummary {
  totalStudents: number;
  totalSessions: number;
  presentCount: number;
  absentCount: number;
  lateCount: number;
  excusedCount: number;
  notMarkedCount: number;
  attendanceRate: number;
  absenceRate: number;
}

export interface AbsenceByFiliere {
  filiereId: string;
  filiereName: string;
  filiereCode: string;
  totalStudents: number;
  totalAbsences: number;
  absenceRate: number;
}

export interface AbsenceByModule {
  moduleElementId: string;
  moduleElementName: string;
  moduleName: string;
  totalSessions: number;
  totalAbsences: number;
  absenceRate: number;
}

export interface AcademicParameters {
  id: string;
  tenantId: string;

  // Grading Configuration
  passingGrade: number;
  maxGrade: number;
  gradeDecimals: number;

  // Subject Validation Configuration
  subjectValidationMethod: 'AVERAGE' | 'ALL_COMPONENTS';
  minScorePerComponent: number;

  // Academic Validation Configuration
  semesterValidationMethod: 'AVERAGE' | 'ALL_VALIDATED';
  yearValidationMethod: 'AVERAGE' | 'ALL_VALIDATED';

  // Rattrapage Configuration
  rattrapageEnabled: boolean;
  rattrapageMaxAttempts: number;
  rattrapageGradeCap: number | null;

  // Absence Configuration
  maxAbsencesBeforeAlert: number;
  maxAbsencesBeforeRattrapage: number;
  lateCountsAsAbsence: boolean;
  lateThresholdMinutes: number;

  createdAt: string;
  updatedAt: string;
}

export interface FilterOption {
  value: string;
  label: string;
  isCurrent?: boolean;
}

// Helper to build query params
function buildQueryParams(params: Record<string, unknown>): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });
  return queryParams.toString();
}

// ==================== RATTRAPAGES ====================

export async function getRattrapages(params: {
  academicYearId?: string;
  filiereId?: string;
  groupeId?: string;
  niveau?: number;
  status?: string;
  reason?: string;
  search?: string;
}): Promise<Rattrapage[]> {
  const queryString = buildQueryParams(params);
  return apiGet<Rattrapage[]>(`/academic-management/rattrapages?${queryString}`);
}

export async function getRattrapageSummary(params: {
  academicYearId?: string;
  filiereId?: string;
}): Promise<RattrapageSummary> {
  const queryString = buildQueryParams(params);
  return apiGet<RattrapageSummary>(`/academic-management/rattrapages/summary?${queryString}`);
}

export async function updateRattrapage(id: string, data: {
  examDate?: string;
  examLocation?: string;
  rattrapageGrade?: number;
  status?: string;
  comments?: string;
}): Promise<Rattrapage> {
  return apiPatch<Rattrapage>(`/academic-management/rattrapages/${id}`, data);
}

export async function exportRattrapages(params: {
  academicYearId?: string;
  filiereId?: string;
  format: 'pdf' | 'excel';
}): Promise<Blob> {
  const queryString = buildQueryParams(params);
  const response = await fetch(`${API_BASE}/academic-management/rattrapages/export?${queryString}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'export');
  }

  return response.blob();
}

// ==================== ABSENCES ====================

export async function getAttendanceRecords(params: {
  academicYearId?: string;
  filiereId?: string;
  groupeId?: string;
  niveau?: number;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}): Promise<AttendanceRecord[]> {
  const queryString = buildQueryParams(params);
  return apiGet<AttendanceRecord[]>(`/academic-management/absences?${queryString}`);
}

export async function getAbsenceSummary(params: {
  academicYearId?: string;
  filiereId?: string;
  groupeId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<AbsenceSummary> {
  const queryString = buildQueryParams(params);
  return apiGet<AbsenceSummary>(`/academic-management/absences/summary?${queryString}`);
}

export async function getAbsencesByFiliere(params: {
  academicYearId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<AbsenceByFiliere[]> {
  const queryString = buildQueryParams(params);
  return apiGet<AbsenceByFiliere[]>(`/academic-management/absences/by-filiere?${queryString}`);
}

export async function getAbsencesByModule(params: {
  academicYearId?: string;
  filiereId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<AbsenceByModule[]> {
  const queryString = buildQueryParams(params);
  return apiGet<AbsenceByModule[]>(`/academic-management/absences/by-module?${queryString}`);
}

export async function updateAttendance(id: string, data: {
  status: string;
  justification?: string | null;
}): Promise<AttendanceRecord> {
  return apiPatch<AttendanceRecord>(`/academic-management/absences/${id}`, data);
}

export async function exportAbsences(params: {
  academicYearId?: string;
  filiereId?: string;
  groupeId?: string;
  dateFrom?: string;
  dateTo?: string;
  format: 'pdf' | 'excel';
  viewMode: string;
}): Promise<Blob> {
  const queryString = buildQueryParams(params);
  const response = await fetch(`${API_BASE}/academic-management/absences/export?${queryString}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Erreur lors de l\'export');
  }

  return response.blob();
}

// ==================== PARAMETERS ====================

export async function getAcademicParameters(): Promise<AcademicParameters | null> {
  try {
    return await apiGet<AcademicParameters>('/academic-management/parameters');
  } catch (error) {
    // Handle 404 as null (no parameters configured yet)
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}

export async function saveAcademicParameters(data: Partial<AcademicParameters>): Promise<AcademicParameters> {
  // Only send editable fields, exclude id, tenantId, createdAt, updatedAt
  const { id, tenantId, createdAt, updatedAt, ...editableData } = data as AcademicParameters;
  void id; void tenantId; void createdAt; void updatedAt; // Silence unused variable warnings
  return apiPut<AcademicParameters>('/academic-management/parameters', editableData);
}

// ==================== FILTERS (reuse from other services) ====================

interface AcademicYearResponse {
  id: string;
  name: string;
  isCurrent?: boolean;
}

/**
 * Get academic years from the academic-planning service
 * This endpoint auto-creates the current academic year if it doesn't exist
 */
export async function getAcademicYears(): Promise<FilterOption[]> {
  const data = await apiGet<AcademicYearResponse[]>('/academic-planning/years');
  return data.map((ay) => ({
    value: ay.id,
    label: ay.name,
    isCurrent: ay.isCurrent,
  }));
}

interface FiliereResponse {
  id: string;
  filiere?: string;
  name?: string;
  codeFiliere?: string;
  nombreAnnees?: number;
  isActive?: boolean;
}

export async function getFilieres(campusId?: string, activeOnly: boolean = true): Promise<FilterOption[]> {
  // Use campus-filtered endpoint if campusId provided, always filter active only by default
  const baseEndpoint = campusId
    ? `/filieres/by-campus/${campusId}`
    : '/filieres';

  const endpoint = `${baseEndpoint}?activeOnly=${activeOnly}`;
  const data = await apiGet<FiliereResponse[]>(endpoint);

  // Filter by isActive status
  // Backend already filters by isActive when activeOnly=true, but we double-check here
  // to ensure consistency and handle any edge cases
  const filtered = data.filter((f) => {
    if (!activeOnly) return true;
    return f.isActive === true;
  });

  return filtered.map((f) => ({
    value: f.id,
    label: f.filiere || f.name || 'Sans nom',
    code: f.codeFiliere,
    nombreAnnees: f.nombreAnnees,
  }));
}

interface GroupeResponse {
  id: string;
  name: string;
  niveau?: number;
}

export async function getGroupes(filiereId?: string, academicYearId?: string, campusId?: string): Promise<FilterOption[]> {
  if (!filiereId) {
    return []; // Groups require a filiere to be selected
  }

  const params = new URLSearchParams();
  if (academicYearId) params.append('academicYearId', academicYearId);
  if (campusId) params.append('campusId', campusId);

  const endpoint = `/groups/filieres/${filiereId}${params.toString() ? `?${params}` : ''}`;
  const data = await apiGet<GroupeResponse[]>(endpoint);

  return data.map((g) => ({
    value: g.id,
    label: g.name,
    niveau: g.niveau,
  }));
}
